// Pure helpers for the "All Battery Packs" inventory tab. Kept out of the component so
// the request-shaping logic can be unit-tested without mounting a Redux-wired table.

export const DEFAULT_SEARCH_COLUMN = "customerPackID";

// Must match the backend's SEARCHABLE_COLUMNS (pack_master_db_helper.py). Anything else
// is rejected there with a 400, so never send an unlisted column.
export const SEARCHABLE_COLUMNS = [
    "customerPackID",
    "deviceID",
    "vin",
    "licensePlateNo",
];

// Searching by a vehicle attribute can only match packs that are mapped to a battery,
// since IMEI / chassis / plate are reached through packConfig.
export const VEHICLE_SEARCH_COLUMNS = ["deviceID", "vin", "licensePlateNo"];

/**
 * Build the `filters` object sent to /api/battery-packs.
 *
 * Search values are folded into `filters` keyed by the selected search column — the same
 * convention the other inventory tabs use — so the backend needs no separate search
 * parameter.
 */
export const buildBatteryPackRequestFilters = (searchTerm, selectedFilters) => {
    const searchColumn = searchTerm?.selectedSearchColumn || DEFAULT_SEARCH_COLUMN;
    const searchValues = searchTerm?.selectedDevices || [];

    return {
        ...(searchValues.length > 0 && { [searchColumn]: searchValues }),
        ...(selectedFilters || {}),
    };
};

/**
 * True when the current search + filter combination can never return a row: a
 * vehicle-attribute search implies "mapped", so pairing it with Mapping Status =
 * Unmapped (and nothing else) is empty by definition.
 *
 * Used to show an explanatory empty state rather than silently overriding the user's
 * filter selection.
 */
export const isImpossibleSearchFilterCombination = (searchTerm, selectedFilters) => {
    const searchColumn = searchTerm?.selectedSearchColumn;
    const hasVehicleSearch =
        VEHICLE_SEARCH_COLUMNS.includes(searchColumn) &&
        (searchTerm?.selectedDevices || []).length > 0;

    if (!hasVehicleSearch) return false;

    const mappingStatus = selectedFilters?.mappingStatus || [];
    return mappingStatus.length === 1 && mappingStatus[0] === "unmapped";
};
